import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { Subject, noop } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { DashpivotEvent, EventTypes } from '@site-mate/dashpivot-shared-library';

import { TemplatesService } from 'app/apps/templates.service';
import { FormService } from 'app/form/form.service';
import { IntercomService } from 'app/intercom/intercom.service';
import { MenuService } from 'app/layout/menu.service';
import { SegmentService } from 'app/segment/segment.service';
import { TemplateRouteContext } from 'app/shared/model/template-route-context.model';
import { TemplateWeb } from 'app/shared/model/template.model';
import { RouteParamsService } from 'app/shared/service/route-params.service';
import { TimingMarkerService } from 'app/shared/service/timing-marker.service';

// TODO: This component should be removed and move the load of the pages/redirect to the view components or resolvers
@Component({
  selector: 'cc-template-forms',
  templateUrl: './template-forms.component.html',
  styleUrls: ['./template-forms.component.scss'],
})
export class TemplateFormsComponent implements OnInit, OnDestroy {
  templateId: string;
  context: TemplateRouteContext;
  loading = true;

  private unSubscribe = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly templatesService: TemplatesService,
    private readonly formsService: FormService,
    private readonly routeParamsService: RouteParamsService,
    private readonly menuService: MenuService,
    private readonly intercomService: IntercomService,
    private readonly timingMarkerService: TimingMarkerService,
    private readonly segmentService: SegmentService,
    private tagManagerService: GoogleTagManagerService,
  ) {}

  ngOnInit(): void {
    void this.tagManagerService.addGtmToDom().catch(noop);
    this.context = this.route.parent.snapshot.data.context;

    if (this.context === TemplateRouteContext.Library) {
      this.segmentService.stopTracking();
      this.intercomService.hide();
    }

    this.retrieveTemplate();
  }

  retrieveTemplate() {
    this.templateId = this.route.snapshot.paramMap.get('templateId');

    const timingMarker = this.timingMarkerService.start(
      new DashpivotEvent(EventTypes.TemplateLoaded, { templateId: this.templateId }),
    );

    this.templatesService.byId(this.templateId, { templateLightweight: true }).subscribe(
      (template) => {
        this.timingMarkerService.finish(timingMarker);

        if (this.context !== TemplateRouteContext.Library) {
          this.menuService.updateSelectedAppSubject.next(template);
        }

        this.routeParamsService.set('template', template);
        this.loading = false;

        if (this.context === TemplateRouteContext.Library) {
          this.navigateToLatestForm(template);
        } else {
          this.navigateToFormsList(template);
        }

        this.router.events
          .pipe(
            takeUntil(this.unSubscribe),
            filter((event) => event instanceof NavigationEnd),
          )
          .subscribe(() => {
            this.navigateToFormsList(template);
          });
      },
      (error) => {
        this.timingMarkerService.finishWithFailure(timingMarker, error);
        this.loading = false;
      },
    );
  }

  private navigateToLatestForm(template: TemplateWeb) {
    this.formsService
      .getLatestFormId(this.templateId)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe({
        next: ({ _id }) => {
          void this.router.navigate([_id], {
            relativeTo: this.route,
            replaceUrl: true,
          });
        },
        error: () => {
          this.navigateToFormsList(template);
        },
      });
  }

  private navigateToFormsList(template) {
    if (this.route.children.length) {
      return;
    }
    // org templates should only open in register view
    if (template.isOrganisationTemplate) {
      void this.router.navigate(['register'], {
        relativeTo: this.route,
        replaceUrl: true,
      });
      // check if the template has columns then it should be a workflow template
    } else if (template.columns && template.columns.length) {
      void this.router.navigate(['workflow'], {
        relativeTo: this.route,
        replaceUrl: true,
      });
      // else this template should display in timeline view
    } else {
      void this.router.navigate(['timeline'], {
        relativeTo: this.route,
        replaceUrl: true,
      });
    }
  }

  ngOnDestroy(): void {
    this.menuService.updateSelectedAppSubject.next();
    this.routeParamsService.set('template', null);
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }
}
